import React, { useEffect } from 'react';
import Message from './Message';
import DateHeader from './DateHeader';
import { useIntersectionObserver } from 'usehooks-ts';
import PrimaryLoader from '../../components/common/PrimaryLoader'; 
import InlineLoader from '../../components/loader/InlineLoader'; 

const MessagesList = ({ 
  messages, 
  messagesContainerRef, 
  isLoading, 
  isFetching,
  loadMoreMessages, 
  isFirstLoad, 
  hasMoreMessages,
  onResolveAttention,
  onRevokeAttention
}) => {
  // Use the ref directly from the hook instead of creating a separate ref
  const { ref: intersectingElementRef, isIntersecting } = useIntersectionObserver({
    root: messagesContainerRef?.current,
    threshold: 0.5,
    rootMargin: '0px',
  });
  
  // Load more messages when user scrolls to top and there are more to load
  useEffect(() => {
    if (isIntersecting && !isLoading && !isFetching && hasMoreMessages) {
      // load more messages
      loadMoreMessages();
    }
  }, [isIntersecting, isLoading, isFetching, loadMoreMessages, hasMoreMessages]);
  
  // Show loader
  if ((isLoading || isFetching) && (!messages || messages.length === 0)) {
    return (
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-auto p-4 flex flex-col items-center justify-center bg-gray-100"
      >
          <div className="flex flex-col items-center space-y-4">
            <PrimaryLoader />
            <p className="text-gray-500">Loading chat...</p>
          </div>
      </div>
    );
  }
  
  if (!messages || !Array.isArray(messages) || messages.length === 0) {
    return (
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-auto p-4 flex flex-col items-center justify-center bg-gray-100"
      >
        <p className="text-gray-500">No messages yet</p>
      </div>
    );
  }
  
  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    // Use created_on from API response
    const date = new Date(message.created_on);
    date.setHours(0, 0, 0, 0);
    const dateKey = date.toISOString();
    
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    
    groups[dateKey].push(message);
    return groups;
  }, {});

  // Sort date groups chronologically
  const sortedDates = Object.keys(groupedMessages).sort();

  return (
    <div 
      ref={messagesContainerRef}
      className="flex-1 overflow-auto p-4 flex flex-col space-y-2 bg-gray-100"
    >
      {/* Show intersection element with loader when loading more messages */}
      <div 
        ref={intersectingElementRef} 
        className="text-center py-2 h-[50px] flex justify-center items-center"
      >
        {isLoading ? <InlineLoader className="text-gray-500" /> : ""}
      </div>
      
      {/* Grouped messages by date */}
      {sortedDates.map(dateKey => (
        <React.Fragment key={dateKey}>
          <DateHeader date={dateKey} />
          {groupedMessages[dateKey].map(msg => (
            <Message 
              key={msg.message_id} 
              message={msg}
              onResolveAttention={onResolveAttention}
              onRevokeAttention={onRevokeAttention}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};

export default MessagesList;
